import { writable, get } from "svelte/store";
import { BackgroundService } from "$/services/BackgroundService";
import { LocalStorage } from "$/services/LocalStorage";
import { TenantService, TENANT_STORAGE_KEY } from "$/services/TenantService";
import { TokenStore } from "$/stores/TokenStore.js";
import { getEffectivePricingPlan } from "$/utils/Utils.js";
import { filterCurrentUserFromCollaborators } from "$/utils/Utils.js";
import { sortWorkspaceMembers } from "../components/editor/utils";
import { CacheService, EntityType } from "$/services/CacheService";
import { StoreStatus, StoreStatusState } from "$/models/StoreStatus";

const TenantServiceImpl = chrome?.storage ? BackgroundService : TenantService;
const CacheServiceImpl = chrome?.storage ? BackgroundService : CacheService;

const store = () => {
    // create store
    const { subscribe, set, update } = writable(null);

    return {
        subscribe,
        set,
        update,
        init: async (tenantId) => {
            // Try to get tenants from cache first
            const cachedTenants = await CacheServiceImpl.getAllEntities(
                EntityType.TENANT,
            );
            const localStorageActive =
                await LocalStorage.get(TENANT_STORAGE_KEY);

            // If we have cached tenants, use them for initial state
            if (cachedTenants.length > 0) {
                const loadId = tenantId ?? localStorageActive?.id;
                const active =
                    cachedTenants.find((tenant) => tenant.id === loadId) ||
                    cachedTenants.find((tenant) => tenant.personal === true);

                let state = new StoreStatusState();
                state.status = StoreStatus.UPDATING;

                set({
                    active,
                    activeAuthorities: [],
                    tenants: cachedTenants,
                    collaborators: [],
                    allCollaborators: [],
                    state: state,
                });

                // load async
                TenantStore.load(tenantId);
            } else {
                // load sync
                await TenantStore.load(tenantId);
            }
        },
        load: async (tenantId) => {
            const tenants = await TenantServiceImpl.getTenants();
            const localStorageActive =
                await LocalStorage.get(TENANT_STORAGE_KEY);
            const loadId = tenantId ? tenantId : localStorageActive?.id;
            const active =
                tenants.find((tenant) => tenant.id === loadId) ||
                tenants.find((tenant) => tenant.personal === true);

            const tenantAuthorities =
                await TenantServiceImpl.getTenantAuthorities(active.id);
            let collaborators = [];
            let allCollaborators = [];

            // we get 403 if we try to get collaborators for tenant where user has a role of user
            // so we need to catch the error and set collaborators to empty array
            try {
                allCollaborators =
                    await TenantServiceImpl.getTenantCollaborators(active.id);
                const topicAndClusterCollaborators = allCollaborators.filter(
                    (collaborator) => collaborator.slackWorkspace === "",
                );
                collaborators = filterCurrentUserFromCollaborators(
                    topicAndClusterCollaborators,
                    get(TokenStore).decodedToken.user.id,
                );
            } catch (error) {
                collaborators = [];
            }

            // sort tenants by creation date, personal tenant first
            tenants.sort((a, b) => {
                if (a.personal) return -1;
                if (b.personal) return 1;
                return a.createdOn - b.createdOn;
            });

            let state = new StoreStatusState();
            state.status = StoreStatus.UP_TO_DATE;

            set({
                active,
                activeAuthorities: tenantAuthorities,
                tenants,
                collaborators: collaborators,
                allCollaborators: allCollaborators,
                state: state,
            });

            await LocalStorage.set(TENANT_STORAGE_KEY, active);
        },
        add: async (tenant) => {
            const result = await TenantServiceImpl.createTenant(tenant);
            let store = get(TenantStore);
            set({
                active: store.active,
                activeAuthorities: store.activeAuthorities,
                tenants: [...store.tenants, result],
                collaborators: [],
                allCollaborators: [],
                state: store.state,
            });
            return result;
        },
        edit: async (tenant) => {
            const result = await TenantServiceImpl.updateTenant(tenant);
            let store = get(TenantStore);
            let active = store.active.id === result.id ? result : store.active;

            const updatedTenants = store.tenants.map((oldTenant) =>
                oldTenant.id === tenant.id ? result : oldTenant,
            );

            set({
                ...store,
                active,
                activeAuthorities: store.activeAuthorities,
                tenants: updatedTenants,
            });

            return result;
        },
        delete: async (tenantId) => {
            await TenantServiceImpl.deleteTenant(tenantId);
            await CacheServiceImpl.removeEntity(EntityType.TENANT, tenantId);

            let store = get(TenantStore);

            const updatedTenants = store.tenants.filter(
                (tenant) => tenant.id !== tenantId,
            );
            const active =
                store.active.id === tenantId
                    ? store.tenants.find((tenant) => tenant.personal == true)
                    : store.active;

            set({
                active,
                tenants: updatedTenants,
                // TODO: Check if this should be added
                // activeAuthorities: store.activeAuthorities,
                // collaborators: store.collaborators,
                // allCollaborators: store.allCollaborators,
                // state: store.state
            });

            await LocalStorage.set(TENANT_STORAGE_KEY, active);

            return tenantId;
        },
        createAuthority: async (authority) => {
            let store = get(TenantStore);
            let resultAuthority = await TenantServiceImpl.createTenantAuthority(
                store.active.id,
                authority,
            );

            set({
                ...store,
                activeAuthorities: [
                    ...store.activeAuthorities,
                    resultAuthority,
                ],
            });

            return resultAuthority;
        },
        deleteAuthority: async (authorityId) => {
            let store = get(TenantStore);
            await TenantServiceImpl.deleteTenantAuthority(
                store.active.id,
                authorityId,
            );

            set({
                ...store,
                activeAuthorities: [
                    ...store.activeAuthorities.filter(
                        (a) => a.id !== authorityId,
                    ),
                ],
            });
        },
        setActive: async (tenant) => {
            // save state and reset store
            let store = get(TenantStore);
            set(null);

            // if tenant is not present in JWT (newly added tenant) refresh token
            let jwtAuthorities =
                get(TokenStore).decodedToken.user.tenantAuthorities[tenant.id];
            if (!jwtAuthorities || jwtAuthorities.length === 0)
                await TokenStore.refreshToken();

            // get all tenant authorities
            let tenantAuthorities =
                await TenantServiceImpl.getTenantAuthorities(tenant.id);
            tenantAuthorities = tenantAuthorities
                ? tenantAuthorities
                : store.activeAuthorities;
            let collaborators = [];
            let allCollaborators = [];
            try {
                allCollaborators =
                    await TenantServiceImpl.getTenantCollaborators(tenant.id);
                collaborators = filterCurrentUserFromCollaborators(
                    allCollaborators,
                    TokenStore.decodedToken.user.id,
                );
            } catch (error) {
                collaborators = [];
            }

            let state = new StoreStatusState();
            state.status = StoreStatus.UP_TO_DATE;

            set({
                active: tenant,
                activeAuthorities: tenantAuthorities,
                tenants: store.tenants,
                collaborators: collaborators,
                allCollaborators: allCollaborators,
                state: state,
            });

            // init stores
            await LocalStorage.set(TENANT_STORAGE_KEY, tenant);
        },
        setActiveById: async (tenantId) => {
            let store = get(TenantStore);
            let tenant = store.tenants.find((tenant) => tenant.id === tenantId);
            await TenantStore.setActive(tenant);
        },
        contains: (tenantId) => {
            return get(TenantStore).tenants.find(
                (tenant) => tenant.id === tenantId,
            )
                ? true
                : false;
        },
        getPersonal: () => {
            return get(TenantStore).tenants.find(
                (tenant) => tenant.personal === true,
            );
        },
        logout: async () => {
            TenantStore.set(null);
            await CacheServiceImpl.clearAll();
            await LocalStorage.remove(TENANT_STORAGE_KEY);
        },
        updateTheme: async (theme) => {
            // Update theme in the active tenant
            let store = get(TenantStore);
            let active = store.active;
            active.theme = theme;
            TenantStore.set({ ...store, active });
        },
        updateLogo: async (file) => {
            let store = get(TenantStore);

            let activeTenantWithNewLogo =
                await TenantServiceImpl.uploadTenantLogo(store.active.id, file);

            set({
                ...store,
                active: activeTenantWithNewLogo,
            });
        },
        cancelSubscription: async () => {
            let store = get(TenantStore);
            let active = store.active;

            let subscriptionRequest = {
                subscriptionId: active.subscription?.subscriptionId,
                status: "cancelled",
            };

            let subscription = await TenantServiceImpl.updateSubscription(
                active.id,
                subscriptionRequest,
            );

            active.subscription = subscription;
            set({ ...store, active });

            return subscription;
        },
        updatePricingPlanLocally: (plan) => {
            let store = get(TenantStore);
            let active = store.active;

            // update active
            active.pricingPlan = plan;

            // set expiration date to 31 days from now
            let expirationDate = new Date();
            expirationDate.setDate(expirationDate.getDate() + 31);
            active.expirationDate = expirationDate;

            // update in tenants
            let tenants = store.tenants.map((tenant) =>
                tenant.id === active.id ? active : tenant,
            );

            set({ ...store, active, tenants });
        },
        updateAuthoritiesUser: (user) => {
            let store = get(TenantStore);
            store.activeAuthorities = store.activeAuthorities.map(
                (authority) =>
                    authority.userId === user.id
                        ? {
                              ...authority,
                              user: {
                                  ...authority.user,
                                  firstName: user.firstName,
                                  lastName: user.lastName,
                              },
                          }
                        : authority,
            );

            set(store);
        },

        /**
         * NOTE: If we had getters accepting tenants then reactivity would continue working
         *
         * $: someVariable = TenantStore.isTrial($TenantStore.active); // this is reactive
         * $: someVariable = TenantStore.isTrial(); // this is not reactive
         */

        isTrial: () => {
            let tenant = get(TenantStore)?.active;
            let subscription = tenant?.subscription;

            if (!tenant || !subscription) return false;

            isTeamTrial =
                tenant.personal === false && subscription.status === "on_trial";
            isLegacyTrial =
                tenant.personal === false &&
                !subscription.status &&
                tenant.expirationDate > new Date();
            isPersonalTrial =
                tenant.personal === true && subscription.status === "on_trial";

            return isTeamTrial || isLegacyTrial || isPersonalTrial;
        },
        isActive: () => {
            let subscription = get(TenantStore)?.active?.subscription;
            return subscription?.status === "active";
        },
        isCancelled: () => {
            let subscription = get(TenantStore)?.active?.subscription;
            return subscription?.status === "cancelled";
        },
        isFree: () => {
            let tenant = get(TenantStore)?.active;
            let subscription = tenant?.subscription;

            if (!tenant || !subscription) return false;

            return (
                tenant.personal === true &&
                getEffectivePricingPlan(tenant) === 0
            );
        },
        isExpired: () => {
            let tenant = get(TenantStore)?.active;
            let subscription = tenant?.subscription;

            if (!tenant || !subscription) return false;

            let isTeamExpired =
                tenant.personal === false && subscription.status === "expired";
            let isPlanExpired = getEffectivePricingPlan(tenant) === -1;
            let isPersonalExpired = false; // personal tenants are just downgraded to free plan

            return isTeamExpired || isPlanExpired || isPersonalExpired;
        },
        isExpiring: () => {
            let tenant = get(TenantStore)?.active;
            let subscription = tenant?.subscription;

            if (!tenant || !subscription) return false;

            return (
                subscription.status === "cancelled" ||
                subscription.status === "paused"
            );
        },
        isManagedSubscription: () => {
            let tenant = get(TenantStore)?.active;
            let subscription = tenant?.subscription;

            if (!tenant || !subscription) return false;

            return (
                !tenant.lifetime &&
                subscription.status != null &&
                subscription.subscriptionId != null
            );
        },
        isOwner: (userId) => {
            let activeAuthorities = get(TenantStore)?.activeAuthorities;
            return activeAuthorities &&
                activeAuthorities.find(
                    (authority) =>
                        authority.userId === userId &&
                        authority.authority === "ROLE_OWNER",
                )
                ? true
                : false;
        },
        isPersonal: () => {
            return get(TenantStore)?.active?.personal;
        },
        isCollaborator: (username) => {
            return (
                get(TenantStore)?.collaborators.find(
                    (collaborator) => collaborator.username === username,
                ) !== undefined
            );
        },
        getActiveMembersCount: () => {
            return (
                get(TenantStore)?.activeAuthorities?.filter(
                    (authority) => authority.authority === "ROLE_USER",
                ).length ?? 0
            );
        },
        getMaxMembersCount: () => {
            return get(TenantStore)?.active?.subscription?.maxMembersCount ?? 0;
        },
        getPricingPlan: () => {
            return get(TenantStore)?.active?.pricingPlan;
        },
        getEffectivePricingPlan: () => {
            return getEffectivePricingPlan(get(TenantStore)?.active);
        },
        searchCollaborators: (query) => {
            let collaborators = get(TenantStore)?.allCollaborators ?? [];
            return collaborators.filter(
                (item) =>
                    item.username.toLowerCase().includes(query.toLowerCase()) ||
                    (item.firstName &&
                        item.firstName
                            .toLowerCase()
                            .includes(query.toLowerCase())) ||
                    (item.lastName &&
                        item.lastName
                            .toLowerCase()
                            .includes(query.toLowerCase())),
            );
        },
        addCollaborator: async (collaborator) => {
            // check if collaborator already exists
            let store = get(TenantStore);
            let collaborators = store.collaborators;
            let existingCollaborator = collaborators.find(
                (c) => c.username === collaborator.email,
            );
            if (existingCollaborator) return existingCollaborator;

            // add collaborator optimistically to the store
            collaborators.push(collaborator);
            set({ ...store, collaborators });

            await TenantStore.refreshCollaboratorsList();
        },
        refreshCollaboratorsList: async () => {
            if (!TenantStore.isOwner(get(TokenStore).decodedToken.user.id))
                return;

            // no optimistic removal since we have no info whether collaborator has access to some other topic or snippet
            let store = get(TenantStore);
            const allCollaborators =
                await TenantServiceImpl.getTenantCollaborators(store.active.id);
            const topicAndClusterCollaborators = allCollaborators.filter(
                (collaborator) => !collaborator.slackWorkspaces.length,
            );
            const newCollaborators = filterCurrentUserFromCollaborators(
                topicAndClusterCollaborators,
                get(TokenStore).decodedToken.user.id,
            );
            set({ ...store, collaborators: newCollaborators });
        },
        groupCollaboratorsByWorkspace: (items) => {
            // collaborators are either provided by the caller or fetched from the store or empty array
            const collaborators =
                items ?? get(TenantStore)?.allCollaborators ?? [];

            return collaborators.reduce((workspaces, collaborator) => {
                if (
                    collaborator.slackWorkspaces &&
                    collaborator.slackWorkspaces.length
                )
                    collaborator.slackWorkspaces.forEach((workspace) =>
                        TenantStore.addCollaboratorToWorkspace(
                            workspaces,
                            collaborator,
                            workspace,
                        ),
                    );

                if (collaborator.isMember) {
                    let localWorkpsace = {
                        id: null,
                        name: "Non-slack members",
                    };
                    TenantStore.addCollaboratorToWorkspace(
                        workspaces,
                        collaborator,
                        localWorkpsace,
                    );
                }

                return workspaces;
            }, []);
        },
        addCollaboratorToWorkspace: (workspaces, collaborator, workspace) => {
            let existingSlackWorkspace = workspaces.find(
                (existingWorkspace) => existingWorkspace.id === workspace.id,
            );
            if (existingSlackWorkspace === undefined) {
                workspaces.push({
                    id: workspace.id,
                    name: workspace.name,
                    icon: workspace.icon,
                    members: [
                        TenantStore.packWorkspaceMember(
                            workspace,
                            collaborator,
                        ),
                    ],
                });
            }
            //TODO: remove check after migration period (there should be no duplicates of collobartors in api response)
            else if (
                existingSlackWorkspace.members.find(
                    (el) => el.id === collaborator.id,
                ) === undefined
            ) {
                existingSlackWorkspace.members.push(
                    TenantStore.packWorkspaceMember(workspace, collaborator),
                );
                existingSlackWorkspace.members = sortWorkspaceMembers(
                    existingSlackWorkspace.members,
                );
            }
        },
        packWorkspaceMember: (workspace, collaborator) => {
            return {
                id: collaborator.id,
                firstName: collaborator.firstName,
                lastName: collaborator.lastName,
                username: collaborator.username,
                isMember: collaborator.isMember,
                workspaceId: workspace.id,
                slackUserId: workspace.slackUserId,
            };
        },
    };
};

export const TenantStore = store();

TenantStore.subscribe(async (store) => {
    if (store?.tenants) {
        await CacheServiceImpl.setEntities(EntityType.TENANT, store.tenants);
    }

    if (store?.active) {
        await CacheServiceImpl.setEntity(
            EntityType.TENANT,
            store.active,
            store.active.id,
        );
    }
});
